<template>
  <div
    class="onboarding-feature-card"
    :class="{ selected: selected }"
    @click="$emit('click')"
  >
    <!-- Network icon badge (top right) -->
    <div v-if="feature.network" class="onboarding-feature-network-badge">
      <img :src="getNetworkIcon(feature.network)" :alt="feature.network" />
    </div>
    
    <div class="onboarding-feature-content">
      <h3 class="onboarding-feature-title">
        {{ feature.title }}
        <svg v-if="feature.network" class="native-icon" width="18" height="18" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" stroke-width="3" stroke="none" fill="#555e75"><path d="M37.34,7.36a.12.12,0,0,1,.18.13c-.47,1.86-2.78,12.63,5.57,19.62,8.16,6.84,8.41,17.13,2.33,24-7.27,8.23-19.84,6.78-25.25,1.37C16.36,48.69,9.44,36.33,21.29,26a.1.1,0,0,1,.16,0c.29,1.23,2.3,9,7.66,10,.25,0,.37-.11.25-.34C27.78,32.6,20.66,17,37.34,7.36Z" stroke-linecap="round"/></svg>
      </h3>
      <p v-if="feature.description" class="onboarding-feature-description" v-html="feature.description">
      </p>
      
      <!-- Feature badges for native features -->
      <div v-if="feature.badges && feature.badges.length > 0" class="onboarding-feature-badges">
        <span
          v-for="(badge, index) in feature.badges"
          :key="index"
          class="onboarding-feature-badge"
          :data-tooltip="getBadgeDescription(badge)"
        >
          {{ getBadgeLabel(badge) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { getNetworkIcon } from '../utils/networkMaps';

export default {
  name: 'FeatureCard',

  props: {
    feature: {
      type: Object,
      required: true
    },
    selected: {
      type: Boolean,
      default: false
    }
  },

  methods: {
    getNetworkIcon(network) {
      return getNetworkIcon(network)
    },
    
    getBadgeLabel(badge) {
      // Support both string and object format
      return typeof badge === 'object' ? badge.label : badge
    },
    
    getBadgeDescription(badge) {
      // Return description for tooltip, or empty string if not available
      return typeof badge === 'object' && badge.description ? badge.description : ''
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/vars.scss';
@import '../variables.scss';

.onboarding-feature-card {
  position: relative;
  padding: 20px;
  border: 1px solid $border-ui;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 0; // Allow card to shrink in grid
  overflow: visible; // Allow tooltips to overflow

  &:hover {
    border-color: darken($border-ui, 10%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &.selected {
    border-color: $primary-color;
    background-color: $bg-ui-selected;

    .onboarding-feature-title,
    .onboarding-feature-description {
      color: $primary-color;
    }
  }
}

.onboarding-feature-network-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 17px;
    height: 17px;
    object-fit: contain;
  }
}

.onboarding-feature-content {
  width: 100%;
}

.onboarding-feature-title {
  font-family: $thicccboi;
  font-size: 16px;
  font-weight: 700;
  color: $secondary-text-color;
  margin-bottom: 6px;
  line-height: 1.3;
  word-wrap: break-word; // Break long words
  overflow-wrap: break-word;
  
  .native-icon {
    display: inline-block;
    vertical-align: text-bottom;
    vertical-align: -2px;
    margin-left: -2px;
  }
}

.onboarding-feature-description {
  font-family: $avenirnext;
  font-size: 13px;
  font-weight: 400;
  color: $secondary-text-color;
  line-height: 1.3;
  word-wrap: break-word; // Break long words
  overflow-wrap: break-word;
  margin-bottom: 0;
}

.onboarding-feature-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}

.onboarding-feature-badge {
  position: relative;
  padding: 4px 10px;
  background: $bg-ui-selected;
  // background: #f2f2f2;
  color: $primary-color;
  // color: $secondary-text-color;
  // border: 1px solid $secondary-text-color;
  font-family: $avenirnext;
  font-size: 12px;
  font-weight: 500;
  border-radius: 12px;
  text-align: center;
  white-space: nowrap;
  transition: all 0.1s ease;
  
  &:hover {
    transform: translateY(-1px);
  }
  
  // Custom tooltip
  &[data-tooltip]:not([data-tooltip=""]) {
    &::before {
      content: attr(data-tooltip);
      position: absolute;
      bottom: calc(100% + 8px);
      left: 50%;
      transform: translateX(-50%) translateY(-4px);
      padding: 6px 10px;
      background: #424242;
      color: #fff;
      font-size: 12px;
      font-weight: 500;
      border-radius: 6px;
      white-space: normal;
      max-width: 300px;
      min-width: 120px;
      text-align: center;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.1s ease, transform 0.1s ease;
      z-index: 1000;
    }
    
    &::after {
      content: '';
      position: absolute;
      bottom: calc(100% - 2px);
      left: 50%;
      transform: translateX(-50%) translateY(-4px);
      border: 6px solid transparent;
      border-top-color: #424242;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.1s ease, transform 0.1s ease;
      z-index: 1000;
    }
    
    &:hover::before,
    &:hover::after {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
}

// When card is selected, make badges more prominent
.onboarding-feature-card.selected {
  .onboarding-feature-badge {
    background: $primary-color;
    color: white;
    border: none;
  }

 .native-icon {
    fill: $primary-color;
  }
}

// Responsive
@media (max-width: 768px) {
  .onboarding-feature-card {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 20px 16px;
    min-height: 100px;
  }

  .onboarding-feature-title {
    font-size: 15px;
  }

  .onboarding-feature-description {
    font-size: 13px;
  }
}
</style>
